// ─────────────────────────────────────────────
// CTArc Portal — Response Parser v3
// Handles fenced ```json``` blocks AND bare leading JSON objects.
// Routes by event type: checkpoint_evaluation, profile_complete.
// ─────────────────────────────────────────────

const repairTrailingCommas = s => s.replace(/,(\s*[}\]])/g, '$1');

// Heuristic repair for unescaped double-quotes inside string values — the most
// common reason a model-emitted quiz fails JSON.parse (e.g. a question like
// What does "overclocking" mean?). Walks the string tracking in/out of a string
// literal; a `"` inside a string is treated as the closing delimiter only when
// the next non-space char is structural (: , } ] or end). Any other inner `"`
// is escaped. Not a full JSON repair, but it rescues the dominant failure case.
function repairUnescapedQuotes(s) {
  let out = '', inStr = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (!inStr) {
      out += ch;
      if (ch === '"') inStr = true;
      continue;
    }
    if (ch === '\\') { out += ch + (s[i + 1] ?? ''); i++; continue; } // keep escapes intact
    if (ch === '"') {
      let j = i + 1;
      while (j < s.length && /\s/.test(s[j])) j++;
      const next = s[j];
      if (next === undefined || next === ':' || next === ',' || next === '}' || next === ']') {
        out += ch; inStr = false;          // legitimate closing quote
      } else {
        out += '\\"';                      // unescaped inner quote → escape it
      }
      continue;
    }
    out += ch;
  }
  return out;
}

// Repair mismatched / missing closing brackets. The model intermittently drops
// the `]` that closes the quiz `questions` array, emitting `[{…},{…}}}` instead
// of `[{…},{…}]}}` (JSON.parse: "expected ',' or ']' after array element"). The
// failure is deterministic — every retry reproduces the same broken bytes — so
// the only reliable recovery is to repair it here. Walks the string tracking
// string-literal context + a bracket stack; when a closer doesn't match the
// bracket on top of the stack, it inserts the correct closer(s) first, then
// closes anything left open at the end. Leaves well-formed JSON byte-identical.
function repairBrackets(s) {
  const closerFor = { '{': '}', '[': ']' };
  const stack = [];
  let out = '', inStr = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inStr) {
      out += ch;
      if (ch === '\\') { out += s[i + 1] ?? ''; i++; continue; } // keep escapes intact
      if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') { inStr = true; out += ch; continue; }
    if (ch === '{' || ch === '[') { stack.push(ch); out += ch; continue; }
    if (ch === '}' || ch === ']') {
      // Insert the closer(s) for any bracket(s) that should have closed before
      // this one (e.g. a missing `]` before `}}`), then emit this closer.
      while (stack.length && closerFor[stack[stack.length - 1]] !== ch) {
        out += closerFor[stack.pop()];
      }
      if (stack.length) stack.pop();
      out += ch;
      continue;
    }
    out += ch;
  }
  while (stack.length) out += closerFor[stack.pop()]; // close anything left open
  return out;
}

// Tolerant JSON.parse. Tries, in order: raw, trailing-comma repair,
// unescaped-quote repair, both together, then bracket repair (alone and stacked
// on the other repairs) for the dropped-`]` case.
function tryParseJSON(str) {
  try { return JSON.parse(str); } catch { /* fall through */ }
  try { return JSON.parse(repairTrailingCommas(str)); } catch { /* fall through */ }
  try { return JSON.parse(repairUnescapedQuotes(str)); } catch { /* fall through */ }
  try { return JSON.parse(repairUnescapedQuotes(repairTrailingCommas(str))); } catch { /* fall through */ }
  try { return JSON.parse(repairBrackets(str)); } catch { /* fall through */ }
  try { return JSON.parse(repairBrackets(repairUnescapedQuotes(repairTrailingCommas(str)))); } catch { /* fall through */ }
  return null;
}

// Layer 3 — impossible-to-leak guarantee. Even if every extraction strategy
// failed, no raw control artifact (delimiters, bare quiz/eval JSON) may reach
// the chat bubble. Worst case the learner sees prose with no quiz; the
// fail-closed renderer then drives the repair retry.
function sanitizeResidualMarkers(display) {
  const markers = [
    /\[\/?QUIZ_JSON_START\][\s\S]*?\[\/?QUIZ_JSON_END\]/g,   // matched delimiters (tolerate slashed variants e.g. [/QUIZ_JSON_END])
    /\[\/?QUIZ_JSON_START\][\s\S]*$/,                        // unclosed / malformed-close delimiter (truncation)
    /\[\/?PRACTICE_JSON_START\][\s\S]*?\[\/?PRACTICE_JSON_END\]/g,
    /\[\/?PRACTICE_JSON_START\][\s\S]*$/,
    /\[\/?REFERENCE_START\][\s\S]*?\[\/?REFERENCE_END\]/g,   // Adaptive Reference markdown block
    /\[\/?REFERENCE_START\][\s\S]*$/,                        // unclosed reference block
    /\[\/?NOTE_START\][\s\S]*?\[\/?NOTE_END\]/g,             // Learning Notes markdown block
    /\[\/?NOTE_START\][\s\S]*$/,                             // unclosed note block
    /\[\/?EXAMPLE_START\][\s\S]*?\[\/?EXAMPLE_END\]/g,       // Worked-example markdown block
    /\[\/?EXAMPLE_START\][\s\S]*$/,                          // unclosed example block
    /\[\/?DIAGRAM_START\][\s\S]*?\[\/?DIAGRAM_END\]/g,       // inline-SVG diagram block
    /\[\/?DIAGRAM_START\][\s\S]*$/,                          // unclosed diagram block (truncated SVG)
    /\{\s*"quiz"\s*:[\s\S]*$/,                           // bare quiz JSON to end
    /\{\s*"practice"\s*:[\s\S]*$/,                       // bare practice JSON to end
    /\{\s*"checkpoint_evaluation"\s*:[\s\S]*$/,          // bare checkpoint_evaluation to end
  ];
  let cleaned = display;
  for (const m of markers) cleaned = cleaned.replace(m, '');
  return cleaned.replace(/\n{3,}/g, '\n\n').trim();
}

// Shuffle one set of {letter,text} options once and remap the correct letter
// to the new position of the originally-correct option (tracked by object
// reference, not text). Fisher-Yates. Used by both quiz (per question) and
// practice (single) — they share the same {letter,text}+correct schema.
function shuffleOptions(options, correctLetter) {
  if (!Array.isArray(options) || options.length < 2) return { options, correct: correctLetter };
  const correctOpt = options.find(o => o?.letter === correctLetter);
  if (!correctOpt) return { options, correct: correctLetter };
  const shuffled = [...options];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  const newCorrectIdx = shuffled.indexOf(correctOpt);
  return {
    options: shuffled.map((opt, idx) => ({ ...opt, letter: String.fromCharCode(65 + idx) })),
    correct: String.fromCharCode(65 + newCorrectIdx),
  };
}

// Shuffle each quiz question's options once at parse time to break the model's
// correct-answer-first position bias. QuizPanel keeps its letter-based grading.
function shuffleQuizOptions(quiz) {
  if (!quiz?.questions?.length) return quiz;
  return {
    ...quiz,
    questions: quiz.questions.map(q => {
      const { options, correct } = shuffleOptions(q.options, q.correct);
      return { ...q, options, correct };
    }),
  };
}

// Same fix for the singular PracticeCard question — model also places its
// correct option at A disproportionately. PracticeCard's tap grading reads
// `practice.correct`; the remapped letter is correct by reference.
function shufflePracticeOptions(practice) {
  if (!practice?.options?.length) return practice;
  const { options, correct } = shuffleOptions(practice.options, practice.correct);
  return { ...practice, options, correct };
}

export function parseResponse(text) {
  if (!text) return { display: '', video: null };

  let evalResult   = null;
  let profileEvent = null;
  let quizData     = null;
  let practiceData = null;
  let reference    = null;   // Adaptive Reference panel markdown (table / contrast card)
  let noteData     = null;   // Learning Notes markdown (shortcuts, traps, takeaways) — saved to Learning Notes, not chat
  let example      = null;   // Real-world worked-example markdown → typed Example block in the lesson canvas
  let diagram      = null;   // Raw inline SVG → sanitized + themed DiagramBlock in the canvas (visual concepts only)

  // ── Strategy 0p: Hard-delimited PracticeCard artifact ──────
  // Same contract/guarantee as the quiz delimiters: the block is stripped from
  // display unconditionally on match (no raw JSON leak), and practiceData is set
  // only when the inner JSON is a valid practice card.
  // Tolerate slashed/XML-style delimiter variants (e.g. [/PRACTICE_JSON_END]) the
  // model occasionally emits — without them the artifact would be lost.
  const PRACTICE_RE = /\[\/?PRACTICE_JSON_START\]\s*([\s\S]*?)\s*\[\/?PRACTICE_JSON_END\]/;
  const pMatch = text.match(PRACTICE_RE);
  if (pMatch) {
    const pp = tryParseJSON(pMatch[1].trim());
    if (pp?.practice?.question && Array.isArray(pp.practice.options) && pp.practice.options.length) {
      practiceData = pp.practice;
    } else {
      console.warn('CTArc parser: [PRACTICE_JSON] delimiters present but no valid practice inside');
    }
    text = text.replace(pMatch[0], '').trim();
  }

  // ── Reference artifact — raw markdown for the Adaptive Reference panel ──
  // Same strip guarantee as the quiz/practice delimiters: the block is removed from
  // display unconditionally on match (no delimiter leak). reference = inner markdown
  // when non-empty, else null + warn. Inner content is markdown (table / contrast
  // card / cheat sheet), not JSON. Slashed variants tolerated for consistency.
  const REFERENCE_RE = /\[\/?REFERENCE_START\]\s*([\s\S]*?)\s*\[\/?REFERENCE_END\]/;
  const rMatch = text.match(REFERENCE_RE);
  if (rMatch) {
    const inner = rMatch[1].trim();
    if (inner) reference = inner;
    else console.warn('CTArc parser: [REFERENCE] delimiters present but empty inside');
    text = text.replace(rMatch[0], '').trim();
  }

  // ── Note artifact — markdown saved to Learning Notes, not rendered in chat ──
  // Same strip + warn guarantee as reference. noteData = inner markdown when non-
  // empty, else null + warn. The engine appends to a session notes array (and
  // persists to learning_notes when the table is present).
  const NOTE_RE = /\[\/?NOTE_START\]\s*([\s\S]*?)\s*\[\/?NOTE_END\]/;
  const nMatch = text.match(NOTE_RE);
  if (nMatch) {
    const inner = nMatch[1].trim();
    if (inner) noteData = inner;
    else console.warn('CTArc parser: [NOTE] delimiters present but empty inside');
    text = text.replace(nMatch[0], '').trim();
  }

  // ── Example artifact — real-world worked example, markdown, rendered as a typed
  // Example block in the lesson canvas (NOT routed to a side panel). Same strip +
  // warn + slashed-variant guarantee as reference/note. example = inner markdown
  // when non-empty, else null + warn.
  const EXAMPLE_RE = /\[\/?EXAMPLE_START\]\s*([\s\S]*?)\s*\[\/?EXAMPLE_END\]/;
  const eMatch = text.match(EXAMPLE_RE);
  if (eMatch) {
    const inner = eMatch[1].trim();
    if (inner) example = inner;
    else console.warn('CTArc parser: [EXAMPLE] delimiters present but empty inside');
    text = text.replace(eMatch[0], '').trim();
  }

  // ── Diagram artifact (Phase 3) — raw inline SVG, rendered as a sanitized,
  // themed DiagramBlock in the canvas. Same strip + warn + slashed-variant
  // guarantee as the other blocks. The raw SVG string is captured here UNSANITIZED
  // — sanitization happens at render time in svgSanitize.js (fail-closed: a
  // diagram that doesn't survive the allowlist renders nothing, never raw SVG).
  // diagram is set only when an <svg> root is actually present inside the block.
  const DIAGRAM_RE = /\[\/?DIAGRAM_START\]\s*([\s\S]*?)\s*\[\/?DIAGRAM_END\]/;
  const dMatch = text.match(DIAGRAM_RE);
  if (dMatch) {
    const inner = dMatch[1].trim();
    if (inner && /<svg[\s>]/i.test(inner)) diagram = inner;
    else console.warn('CTArc parser: [DIAGRAM] delimiters present but no <svg> inside');
    text = text.replace(dMatch[0], '').trim();
  }

  // ── Strategy 0: Hard-delimited quiz artifact (PRIMARY) ─────
  // The checkpoint contract wraps quiz JSON in [QUIZ_JSON_START]...[QUIZ_JSON_END].
  // The delimiters bound the block precisely, so the block is stripped from the
  // display UNCONDITIONALLY on match — even when the inner JSON is malformed or
  // empty ({}) — which makes a raw-JSON / delimiter leak impossible. quizData is
  // set only when the inner JSON parses to a valid quiz; a malformed/empty block
  // leaves quizData null and the fail-closed renderer drives the repair retry.
  // Tolerate slashed/XML-style delimiter variants the model sometimes emits — most
  // commonly a malformed close [/QUIZ_JSON_END]. Without this the quiz fails to
  // extract and the formal QuizPanel never renders.
  const DELIM_RE = /\[\/?QUIZ_JSON_START\]\s*([\s\S]*?)\s*\[\/?QUIZ_JSON_END\]/;
  const delimMatch = text.match(DELIM_RE);
  if (delimMatch) {
    const parsedQuiz = tryParseJSON(delimMatch[1].trim());
    if (parsedQuiz?.quiz?.questions?.length) {
      quizData = parsedQuiz.quiz;
    } else {
      console.warn('CTArc parser: [QUIZ_JSON] delimiters present but no valid quiz inside');
    }
    // Strip the entire delimited block (delimiters included) regardless of parse
    // success — no raw JSON or stray delimiters ever reach the display layer.
    text = text.replace(delimMatch[0], '').trim();
  }

  // ── Step 1: Extract fenced ```json``` blocks ──────────────
  const fencedPattern = /```json\s*(\{[\s\S]*?\})\s*```/g;
  for (const match of text.matchAll(fencedPattern)) {
    try {
      const parsed = tryParseJSON(match[1]);
      if (parsed?.checkpoint_evaluation) {
        const ev = parsed.checkpoint_evaluation;
        evalResult = {
          correct:                ev.correct === true,
          correctness:            ev.correctness             || null,
          answerQuality:          ev.answer_quality          || null,
          errorType:              ev.error_type              || null,
          targetConcept:          ev.target_concept          || null,
          learnerAnswer:          ev.learner_answer          || null,
          reasoningPattern:       ev.reasoning_pattern       || null,
          shouldAdvance:          ev.should_advance          === true,
          needsRecheck:           ev.needs_recheck           === true,
          remediationStrategy:    ev.remediation?.strategy   || null,
          remediationInstruction: ev.remediation?.instruction || null,
        };
      }
      if (parsed?.event === 'profile_complete') {
        profileEvent = { experienceLevel: parsed.experience_level, timeline: parsed.timeline, pace: parsed.pace, track: parsed.track };
      }
      if (parsed?.quiz?.questions?.length && !quizData) {
        quizData = parsed.quiz;
      }
    } catch (e) {
      console.warn('CTArc parser: malformed fenced JSON', e.message);
    }
  }

  // ── Step 2: Extract bare leading JSON (model outputs raw JSON, no fences) ──
  // Handles both single-line {"checkpoint_evaluation":{...}} and multi-line variants.
  // Use brace-matching to find the JSON boundary reliably.
  let stripped = text.trim();
  if (stripped.startsWith('{')) {
    let depth = 0, end = -1;
    for (let i = 0; i < stripped.length; i++) {
      if (stripped[i] === '{') depth++;
      else if (stripped[i] === '}') { depth--; if (depth === 0) { end = i; break; } }
    }
    if (end > -1) {
      try {
        const jsonPart = stripped.slice(0, end + 1);
        const parsed   = tryParseJSON(jsonPart);
        if (parsed?.checkpoint_evaluation) {
          const ev = parsed.checkpoint_evaluation;
          evalResult = {
            correct:                ev.correct === true,
            correctness:            ev.correctness             || null,
            answerQuality:          ev.answer_quality          || null,
            errorType:              ev.error_type              || null,
            targetConcept:          ev.target_concept          || null,
            learnerAnswer:          ev.learner_answer          || null,
            reasoningPattern:       ev.reasoning_pattern       || null,
            shouldAdvance:          ev.should_advance          === true,
            needsRecheck:           ev.needs_recheck           === true,
            remediationStrategy:    ev.remediation?.strategy   || null,
            remediationInstruction: ev.remediation?.instruction || null,
          };
          stripped = stripped.slice(end + 1).trim();
        }
        if (parsed?.event === 'profile_complete') {
          profileEvent = { experienceLevel: parsed.experience_level, timeline: parsed.timeline, pace: parsed.pace, track: parsed.track };
          stripped = stripped.slice(end + 1).trim();
        }
        if (parsed?.quiz?.questions?.length) {
          quizData = parsed.quiz;
          stripped = stripped.slice(end + 1).trim();
        }
      } catch (e) {
        // Not valid JSON at start — leave as-is
      }
    }
  }

  // ── Step 2b: Scan backward for trailing bare JSON (quiz/eval at end of response) ──
  // Scans all { positions from right to left so nested braces don't fool us.
  // Stops at the first position that parses into a known block.
  //
  // Guard: only runs when the response begins with `{`. When the JSON is
  // embedded mid-response (prose then JSON), Step 2c's marker-based extraction
  // with string-aware brace matching handles it correctly — running Step 2b's
  // depth-only counter first risks mis-identifying boundaries on nested
  // braces and either consuming the wrong substring or leaving the JSON in
  // `stripped` for Step 2c to scrub.
  if (!quizData && stripped.startsWith('{')) {
    let scanIdx = stripped.lastIndexOf('{');
    while (scanIdx > 0) {
      const candidateStr = stripped.slice(scanIdx);
      let depth = 0, end = -1;
      for (let ci = 0; ci < candidateStr.length; ci++) {
        if (candidateStr[ci] === '{') depth++;
        else if (candidateStr[ci] === '}') { depth--; if (depth === 0) { end = ci; break; } }
      }
      if (end > -1) {
        try {
          const parsed = tryParseJSON(candidateStr.slice(0, end + 1));
          if (parsed?.quiz?.questions?.length) {
            quizData = parsed.quiz;
            stripped = stripped.slice(0, scanIdx).trim();
            break;
          }
          if (parsed?.checkpoint_evaluation && !evalResult) {
            const ev = parsed.checkpoint_evaluation;
            evalResult = {
              correct:                ev.correct === true,
              correctness:            ev.correctness             || null,
              answerQuality:          ev.answer_quality          || null,
              errorType:              ev.error_type              || null,
              targetConcept:          ev.target_concept          || null,
              learnerAnswer:          ev.learner_answer          || null,
              reasoningPattern:       ev.reasoning_pattern       || null,
              shouldAdvance:          ev.should_advance          === true,
              needsRecheck:           ev.needs_recheck           === true,
              remediationStrategy:    ev.remediation?.strategy   || null,
              remediationInstruction: ev.remediation?.instruction || null,
            };
            stripped = stripped.slice(0, scanIdx).trim();
            break;
          }
        } catch (e) { /* keep scanning leftward */ }
      }
      scanIdx = stripped.lastIndexOf('{', scanIdx - 1);
    }
  }

  // ── Step 2c: Direct marker lookup for quiz JSON anywhere in the response ──
  // Robust to quiz JSON appearing mid-text with surrounding prose, and to
  // braces inside string values (string-aware brace matching). Step 2b's
  // depth-only scan from the last `{` misses those cases — the quiz text
  // ends up rendered raw in chat.
  if (!quizData) {
    // Priority-ordered. The promptCompiler instructs the model to emit
    // {"quiz":{"type":"checkpoint",...} — that's the contract. If the model
    // deviates (no type field), fall back to the lenient {"quiz": prefix so
    // we still extract.
    const markers = [
      '{"quiz":{"type":"checkpoint"',
      '{ "quiz":{"type":"checkpoint"',
      '{"quiz":',
      '{ "quiz":',
    ];
    let bestStart = -1;
    for (const m of markers) {
      const idx = stripped.indexOf(m);
      if (idx !== -1) { bestStart = idx; break; }
    }
    if (bestStart !== -1) {
      let depth = 0, end = -1, inStr = false, escape = false;
      for (let i = bestStart; i < stripped.length; i++) {
        const ch = stripped[i];
        if (escape) { escape = false; continue; }
        if (ch === '\\' && inStr) { escape = true; continue; }
        if (ch === '"') { inStr = !inStr; continue; }
        if (inStr) continue;
        if (ch === '{') depth++;
        else if (ch === '}') {
          depth--;
          if (depth === 0) { end = i; break; }
        }
      }
      if (end !== -1) {
        try {
          const candidate = stripped.slice(bestStart, end + 1);
          const parsed    = tryParseJSON(candidate);
          if (parsed?.quiz?.questions?.length) {
            quizData = parsed.quiz;
            stripped = (stripped.slice(0, bestStart) + stripped.slice(end + 1))
              .replace(/\n{3,}/g, '\n\n')
              .trim();
          }
        } catch (e) {
          console.warn('CTArc parser: quiz marker found but JSON.parse failed:', e.message);
        }
      }
    }
  }

  // ── Step 2d: Global sweep for any remaining bare JSON blocks ──────────────
  // Steps 2/2b/2c each strip only the single block they match. A response that
  // carries more than one bare block — a checkpoint_evaluation AND a quiz, or a
  // duplicated quiz — leaves the companion block in `stripped`, where it renders
  // raw in the chat bubble. This sweep excises EVERY remaining bare JSON object
  // that parses into a known block type (checkpoint_evaluation / quiz /
  // profile_complete), wherever it sits, using string-aware brace matching so
  // braces inside string values don't throw off the boundary. Data fields are
  // backfilled only when an earlier step didn't already capture them; unknown
  // `{...}` spans (e.g. legitimate prose) are skipped, never removed.
  {
    let searchFrom = 0;
    while (true) {
      const open = stripped.indexOf('{', searchFrom);
      if (open === -1) break;

      let depth = 0, end = -1, inStr = false, escape = false;
      for (let i = open; i < stripped.length; i++) {
        const ch = stripped[i];
        if (escape) { escape = false; continue; }
        if (ch === '\\' && inStr) { escape = true; continue; }
        if (ch === '"') { inStr = !inStr; continue; }
        if (inStr) continue;
        if (ch === '{') depth++;
        else if (ch === '}') { depth--; if (depth === 0) { end = i; break; } }
      }
      if (end === -1) break; // unbalanced from here on — leave the remainder alone

      let parsed = null;
      try { parsed = tryParseJSON(stripped.slice(open, end + 1)); } catch (e) { parsed = null; }

      const isKnown = parsed && (
        parsed.checkpoint_evaluation ||
        parsed.quiz ||
        parsed.event === 'profile_complete'
      );

      if (isKnown) {
        if (parsed.checkpoint_evaluation && !evalResult) {
          const ev = parsed.checkpoint_evaluation;
          evalResult = {
            correct:                ev.correct === true,
            correctness:            ev.correctness             || null,
            answerQuality:          ev.answer_quality          || null,
            errorType:              ev.error_type              || null,
            targetConcept:          ev.target_concept          || null,
            learnerAnswer:          ev.learner_answer          || null,
            reasoningPattern:       ev.reasoning_pattern       || null,
            shouldAdvance:          ev.should_advance          === true,
            needsRecheck:           ev.needs_recheck           === true,
            remediationStrategy:    ev.remediation?.strategy   || null,
            remediationInstruction: ev.remediation?.instruction || null,
          };
        }
        if (parsed.quiz?.questions?.length && !quizData) {
          quizData = parsed.quiz;
        }
        if (parsed.event === 'profile_complete' && !profileEvent) {
          profileEvent = { experienceLevel: parsed.experience_level, timeline: parsed.timeline, pace: parsed.pace, track: parsed.track };
        }
        stripped = stripped.slice(0, open) + stripped.slice(end + 1);
        searchFrom = open; // re-scan from the same spot — text shifted left
      } else {
        searchFrom = open + 1; // not a known block — step past this `{`
      }
    }
    stripped = stripped.replace(/\n{3,}/g, '\n\n').trim();
  }

  // ── Step 3: Remove fenced blocks from display ─────────────
  const cleanText = stripped
    .replace(/```json\s*\{[\s\S]*?\}\s*```/g, '')
    .replace(/```json\s*```/g, '')   // orphan fence left after Step 2d excised the inner JSON
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // ── Signal tags ───────────────────────────────────────────
  const advance        = cleanText.includes('[ADVANCE]');
  const reinforce      = cleanText.includes('[REINFORCE]');
  const rewind         = cleanText.includes('[REWIND]');
  const retrievalReady = cleanText.includes('[RETRIEVAL_READY]');
  const profileDone = !!(profileEvent || cleanText.includes('[PROFILE_DONE]'));
  const isInlineCheckpoint = cleanText.includes('[CHECKPOINT:inline]');
  const isEndCheckpoint    = cleanText.includes('[CHECKPOINT:end]');
  const isCheckpoint       = isInlineCheckpoint || isEndCheckpoint;

  // ── Summary tag ───────────────────────────────────────────
  const summaryM = cleanText.match(/\[SUMMARY:\s*([^\]]+)\]/);
  const summary  = summaryM ? summaryM[1].trim() : null;

  // ── Concepts-covered tag ──────────────────────────────────
  // Plain learner-facing concept names taught this turn. Feeds
  // key_concepts_covered so resume/no-repeat have data on teaching turns.
  const conceptsM = cleanText.match(/\[CONCEPTS:\s*([^\]]+)\]/i);
  const concepts  = conceptsM
    ? conceptsM[1].split(';').map(s => s.trim()).filter(Boolean)
    : [];

  // ── Video tag ─────────────────────────────────────────────
  const videoM = cleanText.match(/\[VIDEO:\s*([^\]]+)\]/);
  const video  = videoM ? videoM[1].trim() : null;

  // ── Mastery-signal tag ────────────────────────────────────
  // One learner-facing sentence acknowledging progress → typed MasterySignal
  // block (emerald) in the canvas. Distinct from [SUMMARY:] (internal state).
  const masteryM      = cleanText.match(/\[MASTERY:\s*([^\]]+)\]/i);
  const masterySignal = masteryM ? masteryM[1].trim() : null;

  // ── Next-step tag ─────────────────────────────────────────
  // One sentence bridging to what comes after this concept → typed NextStep block.
  const nextM    = cleanText.match(/\[NEXT:\s*([^\]]+)\]/i);
  const nextStep = nextM ? nextM[1].trim() : null;

  // ── Strip signal tags for display ─────────────────────────
  let display = cleanText
    .replace(/\[ADVANCE\]/g, '')
    .replace(/\[REINFORCE\]/g, '')
    .replace(/\[REWIND\]/g, '')
    .replace(/\[PROFILE_DONE\]/g, '')
    .replace(/\[CHECKPOINT:inline\]/g, '')
    .replace(/\[CHECKPOINT:end\]/g, '')
    .replace(/\[VIDEO:[^\]]+\]/g, '')
    .replace(/\[SUMMARY:[^\]]+\]/g, '')
    .replace(/\[CONCEPTS:[^\]]+\]/gi, '')
    .replace(/\[MASTERY:[^\]]+\]/gi, '')
    .replace(/\[NEXT:[^\]]+\]/gi, '')
    .replace(/\[RETRIEVAL_READY\]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // Layer 3 — final defensive sweep so raw control artifacts can never leak.
  display = sanitizeResidualMarkers(display);

  return {
    display,
    advance,
    quiz: shuffleQuizOptions(quizData),
    practice: shufflePracticeOptions(practiceData),
    reference,
    noteData,
    example,
    diagram,
    masterySignal,
    nextStep,
    reinforce,
    rewind,
    profileDone,
    profileEvent,
    isCheckpoint,
    isInlineCheckpoint,
    isEndCheckpoint,
    video,
    summary,
    concepts,
    retrievalReady,
    evalResult,
    errorType:       evalResult?.errorType           || null,
    remediationPath: evalResult?.remediationStrategy || null,
  };
}
